import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-lg border-b border-background-200/60 shadow-sm"
          : "bg-background-50/85 backdrop-blur-md border-b border-background-200/40"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Hoobuy<span className="text-primary-500">Spreadsheet</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a href="/" className="px-4 py-2 rounded-lg text-sm font-semibold text-foreground-800 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Início
            </a>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-menu-line w-4 h-4 flex items-center justify-center"></i>
                <span>Categorias</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden max-h-[480px] overflow-y-auto">
                  <div className="p-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span>{cat.namePt}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="/artigo/guia-hoobuy-spreadsheet" className="px-4 py-2 rounded-lg text-sm font-semibold text-foreground-800 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Guia de Compras
            </a>
            <a href="#categorias" className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap">
              Produtos
            </a>
            <a href="#seguranca" className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap">
              Segurança
            </a>
            <a href="#comparacao" className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap">
              Comparações
            </a>
            <a href="#faq" className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap">
              FAQ
            </a>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border-2 border-primary-500 text-sm font-semibold text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Hoobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-sm font-bold text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-200/60 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a
              href="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-primary-50 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
              Início
            </a>
            <a
              href="/artigo/guia-hoobuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-primary-50 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-read-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
              Guia de Compras
            </a>

            {/* Mobile Categories Accordion */}
            <div className="border-t border-background-200/60 my-2 pt-2">
              <button
                className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                  Categorias
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-background-200/60 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span>{cat.namePt}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#categorias"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center"></i>
              Produtos
            </a>
            <a
              href="#seguranca"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center"></i>
              Segurança
            </a>
            <a
              href="#comparacao"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-scales-3-line w-5 h-5 flex items-center justify-center"></i>
              Comparações
            </a>
            <a
              href="#faq"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-question-answer-line w-5 h-5 flex items-center justify-center"></i>
              FAQ
            </a>

            <div className="border-t border-background-200/60 pt-3 mt-3 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 rounded-full border-2 border-primary-500 text-sm font-semibold text-primary-600 text-center hover:bg-primary-50 transition-colors cursor-pointer"
              >
                Hoobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 rounded-full bg-primary-500 text-sm font-bold text-white text-center hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}